// ============================================================
// useUnsavedChangesGuard.ts — one hook for every explicit-Save screen.
//
//   const guard = useUnsavedChangesGuard(isDirty)
//   <ModalFrame onClose={() => guard(onClose)} …>
//
// While `isDirty`:
//   • in-app navigation (navigateTo / navigateWithin — sidebar, links,
//     section switches) and browser Back/Forward ask first, via the shared
//     Casevo dialog (navigation/unsavedChanges);
//   • guard(proceed) does the same for the screen's own × / Cancel / close;
//   • refresh / tab close get the browser's native beforeunload prompt.
// Not for auto-save fields — those save on their own.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react'
import { confirmLeave, markDirty } from '../navigation/unsavedChanges'
import { useLeavePageWarning } from './useLeavePageWarning'

export const useUnsavedChangesGuard = (isDirty: boolean): ((proceed: () => void) => void) => {
  const idRef = useRef<symbol>(Symbol('unsaved'))
  const dirtyRef = useRef(isDirty)
  dirtyRef.current = isDirty
  useEffect(() => {
    const id = idRef.current
    markDirty(id, isDirty)
    return () => markDirty(id, false)
  }, [isDirty])
  useLeavePageWarning(isDirty)
  return useCallback((proceed: () => void) => {
    if (!dirtyRef.current) { proceed(); return }
    void confirmLeave().then((leave) => { if (leave) proceed() })
  }, [])
}

// The same guard as a render-nothing component, for screens whose dirty flag
// is only known below an early return (hooks can't run there):
//   {<UnsavedGuard dirty={dirty} />}
export const UnsavedGuard = ({ dirty }: { dirty: boolean }): null => {
  useUnsavedChangesGuard(dirty)
  return null
}

// "Has this draft changed since editing started?" for forms with no saved
// baseline of their own. The draft (any JSON-able value) is snapshotted once
// `active` turns true (form opened / edit mode entered / load finished) —
// one render later, so values a form seeds in its own effects on opening are
// part of the baseline — and compared on every render after. Inactive →
// never dirty.
const PENDING = '(pending)' // never valid JSON, so never equal to a draft
export const useDraftDirty = (active: boolean, draft: unknown): boolean => {
  const json = JSON.stringify(draft)
  const [base, setBase] = useState<string | null>(null)
  useEffect(() => { setBase(active ? PENDING : null) }, [active])
  useEffect(() => { if (base === PENDING) setBase(json) }, [base, json])
  return active && base !== null && base !== PENDING && base !== json
}
